import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { fetchData, fetchGetData, getTranslatedValue, toasterrormsg } from '../../../../reusable'
import { PageTitle } from '../../../../_metronic/layout/core'
import { KTIcon } from '../../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify'
import Loader from '../../component/Loader'
import moment from 'moment'
import { Tr } from '../../../../_metronic/i18n/Tr'
import { useLang } from '../../../../_metronic/i18n/Metronici18n'
import { advertisementStatuses, getStatusBadgeClass, getStatusBadgeColorClass } from '../bannerAdvertisement/AddAdvertisement.constants'

interface TargetMaster {
  sponsertargetmasterId: number
  key: string
  label: string
  inputType: 'input' | 'radio' | 'select'
  description?: string
  iconName?: string
  isActive: boolean
}

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className='row mb-4'>
    <div className='col-5 col-md-4'>
      <span className='text-muted fw-semibold fs-6'>{label}</span>
    </div>
    <div className='col-7 col-md-8'>
      <span className='fw-bold fs-6 text-gray-800'>{value ?? '-'}</span>
    </div>
  </div>
)

const SectionCard = ({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) => (
  <div className='card card-flush mb-6'>
    <div className='card-header pt-5 pb-3' style={{ minHeight: 'unset' }}>
      <div className='d-flex align-items-center gap-3'>
        <span
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: '#e8f4fd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <KTIcon iconName={icon} className='fs-2 text-primary' />
        </span>
        <h5 className='mb-0 fw-bold text-gray-800'>{title}</h5>
      </div>
    </div>
    <div className='card-body pt-4'>{children}</div>
  </div>
)

const ViewSponsureAdsDetail = () => {
  const t = Tr()
  const navigate = useNavigate()
  const location = useLocation()
  const lang = useLang()
  const rowData = (location.state as any) || null

  const vendorData = sessionStorage.getItem('vendor')
  const vendorId = vendorData ? String(JSON.parse(vendorData).vendorId || '') : ''

  const [detail, setDetail] = useState<any>(null)
  const [targetMasters, setTargetMasters] = useState<TargetMaster[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    document.title = 'Insayr | Sponsored Ad Detail'
  }, [])

  useEffect(() => {
    const fetchTargetMasters = async () => {
      try {
        const res: any = await fetchGetData('public/getsponsertargetmasterlist')
        if (String(res?.status) === '200') setTargetMasters(res?.data || [])
      } catch {
        // target labels are optional — fall back to the master id
      }
    }
    fetchTargetMasters()
  }, [])

  useEffect(() => {
    if (!rowData?.sponserId || !vendorId) return
    const fetchDetail = async () => {
      setIsLoading(true)
      try {
        const res: any = await fetchData('public/getvendorsponserdetail', {
          vendorId: String(vendorId),
          sponserId: String(rowData.sponserId),
        })
        if (String(res?.status) === '200') {
          setDetail(res?.data)
        } else {
          toasterrormsg(res?.message || t('Failed to load ad detail'))
        }
      } catch {
        toasterrormsg(t('Failed to load ad detail'))
      } finally {
        setIsLoading(false)
      }
    }
    fetchDetail()
  }, [rowData?.sponserId, vendorId])

  const data = detail || rowData

  const getStatusDisplay = (status: string) =>
    advertisementStatuses.find((s) => s.api === status)?.display || status || '-'

  const getPaymentStatusDisplay = (status: string) =>
    status === 'PAID' ? t('Paid') :
    status === 'PENDING' ? t('Pending') :
    status === 'REFUNDED' ? t('Refunded') :
    status === 'FAILED' ? t('Failed') :
    status

  const formatDate = (d: string | null | undefined) =>
    d ? moment(d).format('DD MMM YYYY') : '-'

  const formatDateTime = (d: string | null | undefined) =>
    d ? moment(d).format('DD MMM YYYY, hh:mm A') : '-'

  const formatAmount = (v: any) =>
    v == null || v === '' ? '-' : Number(v).toLocaleString()

  // TECHNOLOGY -> Technology, MALE -> Male
  const prettify = (v: any) => {
    const s = String(v ?? '')
    return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '-'
  }

  const promotion = Array.isArray(data?.promotionDetail) ? data.promotionDetail[0] : null
  const promotionName = promotion
    ? getTranslatedValue(lang, promotion, '_name') || promotion.en_name || '-'
    : '-'

  const setting = data?.sponserSetting || null
  const steps = data?.completeSteps || null

  // Only targets the vendor actually applied
  const activeTargets: any[] = Array.isArray(data?.targets)
    ? data.targets.filter((tg: any) => tg?.isActive && Array.isArray(tg.value) && tg.value.length > 0)
    : []

  const getTargetMaster = (id: number) =>
    targetMasters.find((m) => Number(m.sponsertargetmasterId) === Number(id))

  const getTargetLabel = (tg: any) => {
    const master = getTargetMaster(tg.sponsertargetmasterId)
    return master?.label || master?.key || t('Target')
  }

  const getTargetValue = (tg: any) => {
    const master = getTargetMaster(tg.sponsertargetmasterId)
    const values: any[] = tg.value || []
    // range fields (e.g. age) come back as [min, max]
    const isRange = master?.inputType === 'input'
      || (values.length === 2 && values.every((v) => !isNaN(Number(v))))
    if (isRange) return `${values[0]} - ${values[1]}`
    return values.map(prettify).join(', ')
  }

  return (
    <>
      {isLoading && <Loader />}
      <ToastContainer />
      <PageTitle breadcrumbs={[]}>{t('Sponsored Ad Detail')}</PageTitle>

      <div className='d-flex align-items-center justify-content-between mb-6'>
        <button
          className='btn btn-light btn-active-light-primary d-flex align-items-center gap-2'
          onClick={() => navigate(-1)}
        >
          <KTIcon iconName='arrow-left' className='fs-4' />
          {t('Back to List')}
        </button>

        {data?.status && (
          <span
            className={`btn btn-sm fw-bold pointer-events-none ${getStatusBadgeClass(data.status)}`}
          >
            {t(getStatusDisplay(data.status))}
          </span>
        )}
      </div>

      {!data && !isLoading && (
        <div className='card card-flush p-10 text-center text-muted fs-5'>
          {t('No ad data found.')}
        </div>
      )}

      {data && (
        <div className='row g-5'>
          {/* Left column */}
          <div className='col-12 col-xl-8'>
            {/* General Info */}
            <SectionCard title={t('Ad Overview')} icon='information-5'>
              <DetailRow label={t('Promotion')} value={promotionName} />
              <DetailRow label={t('Property Type')} value={
                <span className='text-capitalize'>{data.actionType || '-'}</span>
              } />
              <DetailRow label={t('Status')} value={
                data.status ? (
                  <span className={`badge fw-bold text-uppercase ${getStatusBadgeColorClass(data.status)}`}>
                    {t(getStatusDisplay(data.status))}
                  </span>
                ) : '-'
              } />
              {data.created && <DetailRow label={t('Created')} value={formatDateTime(data.created)} />}
              {data.updated && <DetailRow label={t('Updated')} value={formatDateTime(data.updated)} />}
            </SectionCard>

            {/* Duration & Pricing */}
            <SectionCard title={t('Duration & Pricing')} icon='wallet'>
              <div className='row g-4'>
                <div className='col-6 col-md-4'>
                  <div
                    className='p-4 rounded text-center'
                    style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}
                  >
                    <div className='text-muted fs-7 mb-1'>{t('Duration')}</div>
                    <div className='fw-bolder fs-4 text-primary'>
                      {data.duration != null
                        ? `${data.duration} ${t(prettify(data.durationType))}`
                        : '-'}
                    </div>
                  </div>
                </div>
                <div className='col-6 col-md-4'>
                  <div
                    className='p-4 rounded text-center'
                    style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}
                  >
                    <div className='text-muted fs-7 mb-1'>{t('Per Day Price')}</div>
                    <div className='fw-bolder fs-4 text-success'>
                      {formatAmount(data.perDayPrice)}
                    </div>
                  </div>
                </div>
                <div className='col-6 col-md-4'>
                  <div
                    className='p-4 rounded text-center'
                    style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}
                  >
                    <div className='text-muted fs-7 mb-1'>{t('Total Price')}</div>
                    <div className='fw-bolder fs-4 text-dark'>
                      {formatAmount(data.price)}
                    </div>
                  </div>
                </div>
              </div>

              {(data.startDate || data.endDate) && (
                <>
                  <div className='separator my-5' />
                  <DetailRow label={t('Start Date')} value={formatDate(data.startDate)} />
                  <DetailRow label={t('End Date')} value={formatDate(data.endDate)} />
                </>
              )}
            </SectionCard>

            {/* Promotion / Targeting */}
            {Array.isArray(data.targets) && data.targets.length > 0 && (
              <SectionCard title={t('Targeting')} icon='filter'>
                {activeTargets.length === 0 ? (
                  <div className='text-muted fs-6'>{t('No targeting applied — shown to all users.')}</div>
                ) : (
                  activeTargets.map((target: any) => (
                    <div
                      key={target.sponsertargetId}
                      className='d-flex align-items-start gap-3 mb-3'
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: '#009ef7',
                          marginTop: 7,
                          flexShrink: 0,
                        }}
                      />
                      <div>
                        <span className='fw-semibold text-gray-700 text-capitalize'>
                          {t(getTargetLabel(target))}:
                        </span>{' '}
                        <span className='text-gray-600'>{getTargetValue(target)}</span>
                      </div>
                    </div>
                  ))
                )}
              </SectionCard>
            )}

            {/* Payment Info */}
            {(data.paymentMethod || data.transactionNumber || data.paymentStatus != null) && (
              <SectionCard title={t('Payment Info')} icon='credit-cart'>
                <DetailRow label={t('Payment Method')} value={
                  <span className='text-capitalize'>{data.paymentMethod || '-'}</span>
                } />
                <DetailRow label={t('Transaction Number')} value={data.transactionNumber} />
                <DetailRow label={t('Payment Status')} value={
                  data.paymentStatus != null ? (
                    <span className={`badge fw-bold text-uppercase ${
                      data.paymentStatus === 'PAID' ? 'badge-success' :
                      data.paymentStatus === 'PENDING' ? 'badge-warning' :
                      data.paymentStatus === 'REFUNDED' ? 'badge-info' :
                      data.paymentStatus === 'FAILED' ? 'badge-danger' :
                      'badge-secondary'
                    }`}>
                      {getPaymentStatusDisplay(data.paymentStatus)}
                    </span>
                  ) : '-'
                } />
              </SectionCard>
            )}
          </div>

          {/* Right column */}
          <div className='col-12 col-xl-4'>
            {/* Action Type ID / Linked Listing */}
            {(data.actionTypeId != null || data.actionType || promotion) && (
              <SectionCard title={t('Linked Listing')} icon='bookmark'>
                <DetailRow label={t('Business Type')} value={
                  <span className='badge badge-light-primary text-capitalize'>
                    {data.actionType || '-'}
                  </span>
                } />
                {promotion && <DetailRow label={t('Property Name')} value={promotionName} />}
              </SectionCard>
            )}

            {/* Sponsor Setting */}
            {setting && (
              <SectionCard title={t('Sponsor Setting')} icon='screen'>
                <DetailRow label={t('Base Per Day Price')} value={formatAmount(setting.perDayPrice)} />
                <DetailRow label={t('Max Sponsors')} value={setting.maxSponser} />
                <DetailRow label={t('Active')} value={
                  <span className={`badge ${setting.isActive ? 'badge-light-success' : 'badge-light-danger'}`}>
                    {setting.isActive ? t('Yes') : t('No')}
                  </span>
                } />
              </SectionCard>
            )}

            {/* Setup Progress */}
            {steps && (
              <SectionCard title={t('Setup Progress')} icon='check-circle'>
                {Object.keys(steps).map((step) => (
                  <div key={step} className='d-flex align-items-center gap-3 mb-3'>
                    <KTIcon
                      iconName={steps[step] ? 'check-circle' : 'cross-circle'}
                      className={`fs-2 ${steps[step] ? 'text-success' : 'text-muted'}`}
                    />
                    <span className='fw-semibold text-gray-700'>{t(prettify(step))}</span>
                    <span className={`badge ms-auto ${steps[step] ? 'badge-light-success' : 'badge-light-warning'}`}>
                      {steps[step] ? t('Completed') : t('Pending')}
                    </span>
                  </div>
                ))}
              </SectionCard>
            )}

            {/* Rejection Reason */}
            {data.rejectionReason && (
              <div className='card card-flush mb-6 border border-danger'>
                <div className='card-header pt-5 pb-3' style={{ minHeight: 'unset' }}>
                  <div className='d-flex align-items-center gap-3'>
                    <span
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        background: '#fff0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <KTIcon iconName='cross-circle' className='fs-2 text-danger' />
                    </span>
                    <h5 className='mb-0 fw-bold text-danger'>{t('Rejection Reason')}</h5>
                  </div>
                </div>
                <div className='card-body pt-4'>
                  <p className='text-gray-700 mb-0'>{data.rejectionReason}</p>
                </div>
              </div>
            )}

            {/* Quick Summary Card */}
            <div
              className='card card-flush mb-6'
              style={{ background: 'linear-gradient(135deg, #009ef7 0%, #0068a8 100%)' }}
            >
              <div className='card-body p-6'>
                <div className='text-white opacity-75 fs-7 mb-1'>{t('Ad Summary')}</div>
                <div className='text-white fw-bolder fs-2 mb-4'>
                  {formatAmount(data.price)}
                </div>
                <div className='separator border-white opacity-25 mb-4' />
                <div className='d-flex flex-column gap-2'>
                  <div className='d-flex justify-content-between'>
                    <span className='text-white opacity-75 fs-7'>{t('Duration')}</span>
                    <span className='text-white fw-semibold fs-7'>
                      {data.duration != null
                        ? `${data.duration} ${t(prettify(data.durationType))}`
                        : '-'}
                    </span>
                  </div>
                  <div className='d-flex justify-content-between'>
                    <span className='text-white opacity-75 fs-7'>{t('Per Day')}</span>
                    <span className='text-white fw-semibold fs-7'>{formatAmount(data.perDayPrice)}</span>
                  </div>
                  {(data.startDate || data.endDate) && (
                    <>
                      <div className='d-flex justify-content-between'>
                        <span className='text-white opacity-75 fs-7'>{t('Start')}</span>
                        <span className='text-white fw-semibold fs-7'>{formatDate(data.startDate)}</span>
                      </div>
                      <div className='d-flex justify-content-between'>
                        <span className='text-white opacity-75 fs-7'>{t('End')}</span>
                        <span className='text-white fw-semibold fs-7'>{formatDate(data.endDate)}</span>
                      </div>
                    </>
                  )}
                  <div className='d-flex justify-content-between'>
                    <span className='text-white opacity-75 fs-7'>{t('Payment')}</span>
                    <span className='text-white fw-semibold fs-7 text-uppercase'>
                      {data.paymentStatus ? getPaymentStatusDisplay(data.paymentStatus) : '-'}
                    </span>
                  </div>
                  <div className='d-flex justify-content-between'>
                    <span className='text-white opacity-75 fs-7'>{t('Status')}</span>
                    <span className='text-white fw-semibold fs-7 text-uppercase'>
                      {t(getStatusDisplay(data.status))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ViewSponsureAdsDetail
